import { IRefIds } from 'constants/refs';
import { getCacheKey } from 'helpers/cache';
import { del, get, post } from 'services/request';
import { cacheDelete } from 'store/actions/cache';
import store from 'store/store';
import { IItemId } from 'types/content';
import { IHeading, IHeadingEdit } from 'types/headings';
import { BaseModel, IModelOptions } from './BaseModel';
import TranslationManagerModel from './TranslationManagerModel';

export default class HeadingModel extends BaseModel<IHeading, IHeadingEdit> {
  translations: TranslationManagerModel;
  constructor(options: IModelOptions) {
    super(options);
    this.ref = IRefIds.heading;
    this.gridName = 'Headings';
    this.getGridColumns();
    this.translations = new TranslationManagerModel({ ref: this.ref });
  }

  async getGrid() {
    await super.getGrid();
    this.gridData = await get<IHeading[]>(`/headings`);
    this.loadingGrid = false;
    this.render();
  }

  async getGridRow(itemId: number) {
    return await get<IHeading>(`headings/${itemId}/grid-row`);
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async new() {
    await super.new();
    const [item, dialects] = await Promise.all([get<IHeadingEdit>(`headings/new`), this.translations.new()]);
    item.dialects = dialects;
    await this.setItem(item);
    this.render();
  }

  async save() {
    await super.save();
    const { id } = this.itemUpdated;
    if (id === this.NEW_CREATE_ID) {
      await this.createItem();
    } else {
      await this.updateItem();
    }
    this.invalidateCache();
  }

  async updateItem() {
    const { id, dialects } = this.itemUpdated as IHeadingEdit;
    await this.translations.update(id, dialects);
    await Promise.all([this.updateGridRow(id, 'update'), this.loadItemEdit(id)]);
  }

  async createItem() {
    const { dialects } = this.itemUpdated as IHeadingEdit;
    const result = await post<IItemId>(`headings`, {});

    if (result.ok && result.data) {
      const newItemId = result.data.id;
      await this.translations.update(newItemId, dialects);
      await Promise.all([this.updateGridRow(newItemId, 'add'), this.loadItemEdit(newItemId)]);
    }
  }

  async delete() {
    await super.delete();
    const { id } = this.itemUpdated;

    const result = await del(`headings/${id}`);
    if (result.ok) {
      await Promise.all([this.updateGridRow(id, 'remove'), this.new()]);
      this.invalidateCache();
    } else {
      console.error(JSON.stringify(result.data));
    }
  }

  private invalidateCache() {
    store.dispatch(cacheDelete(getCacheKey(IRefIds.heading)));
  }

  async loadItemData(itemId: number) {
    const [item, dialects] = await Promise.all([
      get<IHeadingEdit>(`headings/${itemId}`),
      this.translations.get(itemId),
    ]);
    item.dialects = dialects;
    return item;
  }
}
